import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import NativeBridge from './platform/NativeBridge.jsx';
import ScrollManager from './shell/ScrollManager.jsx';

// Standalone (unauthenticated / token-gated) shells.
import ChargerEntry from './components/ChargerEntry/ChargerEntry.jsx';
import ActivationFlow from './components/Activation/ActivationFlow.jsx';
import OnboardingFlow from './components/Onboarding/OnboardingFlow.jsx';
import SharedOnboardingFlow from './components/Onboarding/SharedOnboardingFlow.jsx';
import ChangePinFlow from './components/ChangePin/ChangePinFlow.jsx';
import Auth from './pages/auth/Auth.jsx';
import Login from './pages/auth/Login.jsx';
import PayBoundary from './pay/PayBoundary.jsx';

// Dashboard shell + pages.
import ProtectedRoute from './components/auth/ProtectedRoute.jsx';
import AppShell from './shell/AppShell.jsx';
import DashboardPage from './pages/dashboard/DashboardPage.jsx';
import FindChargerPage from './pages/charge/FindChargerPage.jsx';
import ActiveSessionPage from './pages/charge/ActiveSessionPage.jsx';
import HistoryPage from './pages/history/HistoryPage.jsx';
import SessionDetailPage from './pages/history/SessionDetailPage.jsx';
import EnergyInsightsPage from './pages/energy-insights/EnergyInsightsPage.jsx';
import VehiclesPage from './pages/vehicles/VehiclesPage.jsx';
import VehicleDetailPage from './pages/vehicles/VehicleDetailPage.jsx';
import VehicleEditPage from './pages/vehicles/VehicleEditPage.jsx';
import ProfilePage from './pages/profile/ProfilePage.jsx';
import SettingsPage from './pages/settings/SettingsPage.jsx';
import AppearanceSettingsPage from './pages/settings/AppearanceSettingsPage.jsx';
import SupportPage from './pages/support/SupportPage.jsx';
import FaqPage from './pages/support/FaqPage.jsx';
import RedeemCodesPage from './pages/redeem-codes/RedeemCodesPage.jsx';
import NotificationsPage from './pages/notifications/NotificationsPage.jsx';
import NotFoundPage from './pages/errors/NotFoundPage.jsx';

/**
 * Route tree for the customer app.
 *
 * The tree is split into three trust / layout groups:
 *
 *   1. Public + token-gated standalone shells — /auth, legacy password
 *      redirects, /onboard/**, /vehicles/change-pin/**, /activate/**.
 *      These render their own top-level chrome.
 *
 *   2. **Standalone charging flow** — /c/:identifier, /station and
 *      /charge/active. Rendered OUTSIDE the dashboard `AppShell`
 *      because the charger overview + kiosk wizard need the full
 *      viewport (no sidebar, no top-bar, no bottom nav). QR deep
 *      links land in this group. Public so a cold scan works.
 *
 *   3. Authenticated dashboard — wrapped by
 *      <ProtectedRoute><AppShell/></ProtectedRoute>. Contains ONLY the
 *      dashboard surfaces (home, /charge discovery, history,
 *      vehicles, profile, settings, support). No charger-flow route
 *      is a child of this group.
 *
 * Layout ownership is deliberately decoupled from authentication:
 * ProtectedRoute enforces auth; AppShell composes the dashboard
 * chrome; the two are separately applied per route.
 */
/**
 * The standalone flows below render outside AppShell and AuthLayout, the
 * two shells that own a <main>. This gives each of them the page's one
 * main landmark without changing their layout (see .gc-flow-main).
 */
function FlowMain({ children }) {
  return <main className="gc-flow-main">{children}</main>;
}

export default function AppRoutes() {
  return (
    <>
      {/*
        NativeBridge is a headless component that lives INSIDE the
        router so it can call useNavigate + useSession + useToast +
        useOptionalNotificationDrawer. It wires:
          - app resume → charging session rehydration
          - deep links (cold + warm) → React Router navigation
          - Android back button → drawer close then history navigation
          - network state → offline / online toast
        See src/platform/NativeBridge.jsx for the full contract.
      */}
      <NativeBridge />
      {/* Route-change scroll reset / POP restore — see ScrollManager. */}
      <ScrollManager />
      <Routes>
      {/* ── 1. Public / unauthenticated shells ────────────────── */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Navigate to="/auth" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/auth" replace />} />
      <Route path="/reset-password" element={<Navigate to="/auth" replace />} />
      <Route path="/verify-email" element={<Navigate to="/auth" replace />} />
      <Route path="/check-email" element={<Navigate to="/auth" replace />} />

      <Route path="/onboard/:token" element={<FlowMain><OnboardingFlow /></FlowMain>} />
      <Route path="/onboard/link/:token" element={<FlowMain><SharedOnboardingFlow /></FlowMain>} />
      <Route path="/vehicles/change-pin/:token" element={<FlowMain><ChangePinFlow /></FlowMain>} />
      <Route path="/activate/:chargerCode" element={<FlowMain><ActivationFlow /></FlowMain>} />

      {/*
        /pay — the payment page (payment recovery). It is NOT rendered in
        this tree: main.jsx boots it in a minimal one (pay/PayRoot.jsx) with
        no telemetry and no session providers. This route only exists for a
        client-side navigation that lands here, and reloads the document.
      */}
      <Route path="/pay" element={<FlowMain><PayBoundary /></FlowMain>} />

      {/* ── 2. Standalone charging flow (OUTSIDE AppShell) ────── */}
      {/*
        /c/:identifier       — public charger-entry route. Accepts both
                               canonical GCEV codes and legacy internal
                               station identifiers printed on pre-GCEV
                               stickers; ChargerEntry hands the raw
                               value to /api/public/chargers/:identifier
                               and forwards to /station on success.
                               Also the QR-code landing URL.
        /station             — legacy kiosk entry that the charging
                               wizard has always used. Query params
                               (?stationId, ?sessionUid) drive it.
        /charge/active       — canonical URL for the active session.
                               The dashboard's ActiveSessionCard links
                               here so both entry paths converge on
                               the same wizard.

        These three MUST NOT be nested under AppShell — the charger
        profile + wizard own the entire viewport.
      */}
      <Route path="/c/:identifier" element={<FlowMain><ChargerEntry /></FlowMain>} />
      <Route path="/station" element={<ActiveSessionPage />} />
      <Route path="/charge/active" element={<ActiveSessionPage />} />

      {/* ── 3. Authenticated dashboard shell ─────────────────── */}
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="/charge" element={<FindChargerPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/history/:sessionUid" element={<SessionDetailPage />} />
        <Route path="/energy-insights" element={<EnergyInsightsPage />} />
        <Route path="/redeem-codes" element={<RedeemCodesPage />} />
        <Route path="/vehicles" element={<VehiclesPage />} />
        <Route path="/vehicles/:vehicleId" element={<VehicleDetailPage />} />
        <Route path="/vehicles/:vehicleId/edit" element={<VehicleEditPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/appearance" element={<AppearanceSettingsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/faq" element={<FaqPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      </Routes>
    </>
  );
}
